import React, { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Card from "@components/Card";
import Div from "@components/Div";
import { H1 } from "@components/Heading";
import FullSizeDialog from "@components/FullSizeDialog";
import PrivacyPolicyFooter from "@components/PrivacyPolicyFooter";
import { Text } from "@components/Text";

import { useToast } from "@hooks/useToast";

import { CandidateLoginHeader } from "./components";
import {
  useCandidateLoginSharedData,
  useCandidateLoginStepper,
} from "./context";

const CandidateLogin = () => {
  const { messages } = useIntl();
  const { candidate_id } = useParams();
  const {
    handleShareCandidateId,
    handleShareErrorHandler,
    handleShareSuccessHandler,
  } = useCandidateLoginSharedData();
  const { currentItem, onStep, currentItemIndex } = useCandidateLoginStepper();
  const { showErrorToast } = useToast();

  const handleSuccessLogin = useCallback(
    successResponse => {
      const { token } = successResponse;

      localStorage.setItem("candidateToken", token);

      onStep(3);
    },
    [onStep]
  );

  const handleError = useCallback(
    error => {
      showErrorToast(error?.message ?? error);
    },
    [showErrorToast]
  );

  useEffect(() => {
    handleShareCandidateId(candidate_id);
    handleShareErrorHandler(handleError);
    handleShareSuccessHandler(handleSuccessLogin);
  }, [
    candidate_id,
    handleError,
    handleShareCandidateId,
    handleShareErrorHandler,
    handleShareSuccessHandler,
    handleSuccessLogin,
  ]);

  useEffect(() => {
    const candidateToken = localStorage.getItem("candidateToken");

    if (!candidateToken) {
      return;
    }

    onStep(3);
  }, [onStep]);

  return (
    <FullSizeDialog header={<CandidateLoginHeader />}>
      <Div
        height="100%"
        display="flex"
        flexDirection="column"
        gridGap={4}
        position="relative"
      >
        <Div
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gridGap={4}
        >
          {currentItemIndex === 0 && (
            <Div
              display="flex"
              flexDirection="column"
              alignItems="center"
              gridGap={2}
              maxWidth="500px"
            >
              <H1 color="var(--blue-dark) !important">
                {messages.title_welcome_to_candidate_portal}
              </H1>

              <Text textAlign="center">
                {messages.message_welcome_to_candidate_portal}
              </Text>
            </Div>
          )}

          <Div
            display="flex"
            width={1}
            alignItems="center"
            justifyContent="center"
          >
            <Card width={1} maxWidth="500px">
              <Div
                px={[3, 3, 4, 4]}
                py="24px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gridGap={["24px", "24px", 4, 4]}
              >
                {currentItem}
              </Div>
            </Card>
          </Div>
        </Div>

        {currentItemIndex !== 2 && <PrivacyPolicyFooter />}
      </Div>
    </FullSizeDialog>
  );
};

export default CandidateLogin;
